<template>
  <div class="vacancy-list-container">
    <!-- Loading and Error States -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading vacancies...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="fetchVacancies" class="retry-btn">Retry</button>
    </div>

    <div v-else>
      <!-- Search and Filter Section with Summary -->
      <div class="top-section">
        <!-- Summary Cards -->
        <div class="summary-cards">
          <div class="summary-card">
            <div class="card-icon">
              <i class="fas fa-user-plus"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Today's Joinings</h3>
              <p class="card-number">{{ todaysJoiningSummary }}</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="card-icon">
              <i class="fas fa-clipboard-list"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Total Vacancies</h3>
              <p class="card-number">{{ totalVacancies }}</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="card-icon urgent-icon">
              <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Urgent Vacancies</h3>
              <p class="card-number">{{ urgentVacanciesCount }}</p>
            </div>
          </div>
        </div>

        <div class="search-filter-section">
          <div class="search-box">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search vacancies..."
              class="search-input"
              @input="handleSearch"
            />
          </div>
          <div class="filters">
            <div class="date-filters">
              <input
                type="date"
                v-model="fromDate"
                class="filter-date"
                @change="handleFilter"
              />
              <span class="date-separator">to</span>
              <input
                type="date"
                v-model="toDate"
                class="filter-date"
                @change="handleFilter"
              />
            </div>
            <select v-model="selectedDepartment" class="filter-select" @change="handleFilter">
              <option value="">All Departments</option>
              <option v-for="dept in departments" :key="dept" :value="dept">
                {{ dept }}
              </option>
            </select>
            <select v-model="selectedStatus" class="filter-select" @change="handleFilter">
              <option value="">All Status</option>
              <option value="urgent">Urgent</option>
              <option value="very-urgent">Very Urgent</option>
            </select>
          </div>
        </div>
      </div>

      <!-- No Results Message -->
      <div v-if="filteredVacancies.length === 0" class="no-results">
        <p>No vacancies found matching your criteria</p>
      </div>

      <!-- Vacancies Table -->
      <div v-else class="table-responsive">
        <table class="vacancy-table">
          <thead>
            <tr>
              <th>SN</th>
              <th>Date</th>
              <th>Site Name</th>
              <th>Dept</th>
              <th>Post Vacant</th>
              <th>Reliever</th>
              <th>Change</th>
              <th>Gender</th>
              <th>Salary</th>
              <th>PF/ESI</th>
              <th>Stay</th>
              <th>W/O FF</th>
              <th>Food</th>
              <th>Location</th>
              <th>Hrs</th>
              <th>Deadline (Operation)</th>
              <th>Current Deadline (Recruitment)</th>
              <th>Missed Deadlines</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(vacancy, index) in filteredVacancies" :key="vacancy._id" 
                :class="{ 'urgent': vacancy.status === 'urgent', 'very-urgent': vacancy.status === 'very-urgent' }">
              <td>{{ startIndex + index + 1 }}</td>
              <td>{{ formatDate(vacancy.vacancyDate) }}</td>
              <td>{{ vacancy.siteName }}</td>
              <td>{{ vacancy.departmentName }}</td>
              <td>{{ vacancy.postVacant }}</td>
              <td>{{ vacancy.relieverName }}</td>
              <td>{{ vacancy.changeType }}</td>
              <td>{{ vacancy.gender }}</td>
              <td>{{ vacancy.salary }}</td>
              <td>{{ vacancy.pfEsi ? 'Yes' : 'No' }}</td>
              <td>{{ vacancy.stay ? 'Yes' : 'No' }}</td>
              <td>{{ vacancy.weeklyOff }}</td>
              <td>{{ vacancy.food }}</td>
              <td>{{ vacancy.location }}</td>
              <td>{{ vacancy.workingHours }}</td>
              <td>{{ formatDate(vacancy.operationDeadline) }}</td>
              <td>{{ formatDate(vacancy.recruitmentDeadline) }}</td>
              <td>{{ vacancy.missedDeadlines }}</td>
              <td>
                <span :class="['status-badge', vacancy.status]">
                  {{ vacancy.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination">
        <button 
          :disabled="currentPage === 1" 
          @click="changePage(currentPage - 1)"
          class="page-btn"
        >
          Previous
        </button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button 
          :disabled="currentPage === totalPages" 
          @click="changePage(currentPage + 1)"
          class="page-btn"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { format } from 'date-fns'

interface Vacancy {
  _id: string
  siteId: {
    _id: string
    siteName: string
  }
  siteName: string
  departmentId: {
    _id: string
  }
  departmentName: string
  postVacant: string
  relieverId?: {
    _id: string
    empName: string
  }
  relieverName?: string
  changeType: string
  gender: string
  salary: number
  pfEsi: boolean
  stay: boolean
  weeklyOff: string
  food: string
  location: string
  workingHours: number
  operationDeadline: string
  recruitmentDeadline: string
  missedDeadlines: number
  status: string
  todayJoining: number
  isActive: boolean
  vacancyDate: string
  createdAt: string
  updatedAt: string
}

interface ApiResponse {
  success: boolean
  count: number
  data: Vacancy[]
  filters: any
  message: string
}

// Replace the sample data with API call
const vacancies = ref<Vacancy[]>([])
const isLoading = ref(true)
const error = ref<string | null>(null)

// Fetch vacancies from API
const fetchVacancies = async () => {
  try {
    isLoading.value = true
    const baseUrl = process.env.VUE_APP_BASE_URL
    if (!baseUrl) {
      throw new Error('API base URL not configured')
    }
    
    const response = await fetch(`${baseUrl}api/get/vacancy`)
    const data: ApiResponse = await response.json()
    
    if (data.success) {
      vacancies.value = data.data
    } else {
      error.value = data.message || 'Failed to fetch vacancies'
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch vacancies'
    console.error('Error fetching vacancies:', err)
  } finally {
    isLoading.value = false
  }
}

// Call fetchVacancies when component mounts
onMounted(() => {
  fetchVacancies()
})

// Filter and Search state
const searchQuery = ref('')
const selectedDepartment = ref('')
const selectedStatus = ref('')
const currentPage = ref(1)
const itemsPerPage = 10

// Add after searchQuery ref
const fromDate = ref('')
const toDate = ref('')

// Computed properties
const departments = computed(() => 
  [...new Set(vacancies.value.map(v => v.departmentName))]
)

const filteredVacancies = computed(() => {
  let filtered = [...vacancies.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(v => 
      v.siteName.toLowerCase().includes(query) ||
      v.departmentName.toLowerCase().includes(query) ||
      v.location.toLowerCase().includes(query)
    )
  }

  // Apply date range filter
  if (fromDate.value && toDate.value) {
    const startDate = new Date(fromDate.value)
    const endDate = new Date(toDate.value)
    endDate.setHours(23, 59, 59) // Include the entire end date

    filtered = filtered.filter(v => {
      const vacancyDate = new Date(v.vacancyDate)
      return vacancyDate >= startDate && vacancyDate <= endDate
    })
  }

  // Apply department filter
  if (selectedDepartment.value) {
    filtered = filtered.filter(v => v.departmentName === selectedDepartment.value)
  }

  // Apply status filter
  if (selectedStatus.value) {
    filtered = filtered.filter(v => v.status.toLowerCase() === selectedStatus.value)
  }

  // Apply pagination
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filtered.slice(start, end)
})

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage)

const totalPages = computed(() => 
  Math.ceil(vacancies.value.length / itemsPerPage)
)

const todaysJoiningSummary = computed(() => {
  return vacancies.value.reduce((sum, vacancy) => sum + (vacancy.todayJoining || 0), 0)
})

const totalVacancies = computed(() => vacancies.value.length)

const urgentVacanciesCount = computed(() => 
  vacancies.value.filter(v => 
    v.status === 'urgent' || v.status === 'very-urgent' || v.status === 'Urgent' || v.status === 'Very Urgent'
  ).length
)

// Methods
const handleSearch = () => {
  currentPage.value = 1
}

const handleFilter = () => {
  currentPage.value = 1
}

const changePage = (page: number) => {
  currentPage.value = page
}

const formatDate = (date: string) => {
  return format(new Date(date), 'dd/MM/yyyy')
}
</script>

<style scoped>
.vacancy-list-container {
  padding: 20px;
}

.top-section {
  margin-bottom: 24px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #e3f2fd;
  color: #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon i {
  font-size: 24px;
}

.urgent-icon {
  background-color: #fff3e0;
  color: #f57c00;
}

.card-content {
  flex: 1;
}

.card-title {
  font-size: 14px;
  color: #666;
  margin: 0 0 4px 0;
}

.card-number {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.search-filter-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-box {
  flex: 1;
  max-width: 300px;
  margin-bottom: 15px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  height: 36px;
}

.filters {
  display: flex;
  gap: 15px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 200px;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 20px;
}

.vacancy-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  font-size: 14px;
}

.vacancy-table th,
.vacancy-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
  white-space: nowrap;
}

.vacancy-table th {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.vacancy-table tbody tr:hover {
  background-color: #f5f5f5;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: capitalize;
  font-weight: 500;
}

.urgent {
  background-color: #fff3cd;
  color: #856404;
}

.very-urgent {
  background-color: #f8d7da;
  color: #721c24;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.page-btn:disabled {
  background: #f8f9fa;
  cursor: not-allowed;
}

.page-info {
  color: #6c757d;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-filter-section {
    flex-direction: column;
  }

  .filters {
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .vacancy-table {
    font-size: 12px;
  }

  .vacancy-table th,
  .vacancy-table td {
    padding: 8px;
  }

  .search-box {
    max-width: 100%;
    margin-bottom: 10px;
  }
}

.loading-state,
.error-state,
.no-results {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-btn {
  margin-top: 15px;
  padding: 8px 16px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
}

.retry-btn:hover {
  background: #2980b9;
}

/* Update status colors based on API values */
.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: capitalize;
  font-weight: 500;
}

tr.urgent .status-badge,
tr .status-badge.urgent {
  background-color: #fff3cd;
  color: #856404;
}

tr.filled .status-badge,
tr .status-badge.filled {
  background-color: #d4edda;
  color: #155724;
}

/* Mobile Responsive Enhancements */
@media (max-width: 768px) {
  .vacancy-list-container {
    padding: 10px;
  }

  .search-filter-section {
    gap: 10px;
  }

  .search-box {
    min-width: 100%;
  }

  .table-responsive {
    margin: 0 -10px;
    width: calc(100% + 20px);
  }

  .vacancy-table {
    font-size: 12px;
  }

  .vacancy-table th,
  .vacancy-table td {
    padding: 6px;
  }

  .status-badge {
    padding: 2px 6px;
    font-size: 11px;
  }

  .pagination {
    flex-wrap: wrap;
    gap: 10px;
  }
}

.summary-cards {
  grid-template-columns: 1fr;
  gap: 12px;
  margin: -10px -10px 15px;
}

.summary-card {
  border-radius: 0;
  padding: 15px;
}

.card-icon {
  width: 40px;
  height: 40px;
}

.card-icon i {
  font-size: 20px;
}

.card-title {
  font-size: 13px;
}

.card-number {
  font-size: 20px;
}

.search-filter-section {
  border-radius: 0;
  padding: 15px;
  margin: 0 -10px;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-date {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.date-separator {
  color: #666;
}

/* Update responsive styles */
@media (max-width: 768px) {
  .date-filters {
    flex-direction: column;
    width: 100%;
  }

  .filter-date {
    width: 100%;
  }

  .date-separator {
    display: none;
  }
}
</style>
